import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { CompanyState } from '../../../../types/company';
import { employeeService, EMPLOYEE_ROLES, EMPLOYEE_ROLE_ORDER, getRoleDisplayName } from '../../../../services/tycoon/employeeService';
import { EMPLOYEE_CONSTANTS } from '../../../../config/gameConstants';
import type { HiringProcess } from '../../../../types/tycoon';

interface EmployeesSectionProps {
  companyState: CompanyState;
  onNavigate?: () => void;
}

export function EmployeesSection({ companyState, onNavigate }: EmployeesSectionProps) {
  const [currentView, setCurrentView] = useState<'employees' | 'hiring'>('employees');
  
  // Add defensive check
  if (!companyState) {
    return (
      <div className="border border-theme-border bg-theme-bg-content/50 rounded-lg relative overflow-hidden h-full">
        <div className="absolute top-0 left-0 right-0 z-10 bg-theme-bg-content p-2 flex items-center justify-between border-b border-theme-border">
          <button className="p-1 text-theme-text-muted" disabled>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={onNavigate}
            className={`font-bold text-sm text-theme-text-secondary ${
              onNavigate ? 'hover:text-theme-info transition-colors cursor-pointer' : ''
            }`}
            disabled={!onNavigate}
          >
            Employees
          </button>
          <button className="p-1 text-theme-text-muted" disabled>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="pt-10 p-3 h-full text-center text-theme-text-muted">
          Loading...
        </div>
      </div>
    );
  }

  // Get employee stats
  const stats = employeeService.getEmployeeStats(companyState.employees || []);
  const hiringProcesses = companyState.hiringProcesses || [];

  const toggleView = () => {
    setCurrentView(currentView === 'employees' ? 'hiring' : 'employees');
  };

  return (
    <div className="border border-theme-border bg-theme-bg-content/50 rounded-lg relative overflow-hidden h-full">
      <div className="absolute top-0 left-0 right-0 z-10 bg-theme-bg-content p-2 flex items-center justify-between border-b border-theme-border">
        <button
          onClick={toggleView}
          className="p-1 text-theme-text-secondary hover:text-theme-text-primary transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          onClick={onNavigate}
          className={`font-bold text-sm text-theme-text-secondary ${
            onNavigate ? 'hover:text-theme-info transition-colors cursor-pointer' : ''
          }`}
          disabled={!onNavigate}
        >
          {currentView === 'employees' ? 'Employees' : 'Hiring Progress'}
        </button>
        <button
          onClick={toggleView}
          className="p-1 text-theme-text-secondary hover:text-theme-text-primary transition-colors"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="pt-10 p-3 h-full overflow-y-auto scroll-smooth scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
        {currentView === 'employees' ? (
          // Employee Stats View
          <div className="space-y-2">
            {EMPLOYEE_ROLE_ORDER.map(role => (
              <div key={role} className="bg-theme-bg-content/80 rounded p-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-theme-text-secondary">{getRoleDisplayName(role)}s</span>
                  <span className="font-semibold text-xs">{stats[role]}</span>
                </div>
              </div>
            ))}
            <div className="bg-theme-bg-content/80 rounded p-2 mt-3 border-t border-theme-border pt-2">
              <div className="flex justify-between items-center">
                <span className="text-xs font-medium text-theme-text-secondary">Total</span>
                <span className="font-bold text-xs text-theme-info">{stats.total}</span>
              </div>
            </div>
          </div>
        ) : (
          // Hiring Progress View
          hiringProcesses.length === 0 ? (
            <div className="text-center text-theme-text-muted text-xs mt-4">
              No active hiring
            </div>
          ) : (
            <div className="space-y-2">
              {hiringProcesses.map((process: HiringProcess, idx: number) => (
                <div key={idx} className="bg-theme-bg-content/80 rounded p-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-theme-text-secondary">
                      {process.count} {EMPLOYEE_ROLES[process.role]?.name}(s)
                    </span>
                    <span className="text-xs text-theme-text-muted">{process.daysRemaining}d</span>
                  </div>
                  <div className="w-full bg-theme-bg-tertiary rounded-full h-1.5">
                    <div 
                      className="bg-theme-info h-1.5 rounded-full transition-all"
                      style={{ 
                        width: `${((EMPLOYEE_CONSTANTS.hiringDays - process.daysRemaining) / EMPLOYEE_CONSTANTS.hiringDays) * 100}%` 
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}